import { ShortCutClassicLayer } from "../../map/layers/ShortCutClassicLayer";
import { FullscreenControl, NavigationControl } from "maplibre-gl";
import * as turf from "@turf/turf";
import { Dictionary } from "../../shared/Dictionary";
import { get, writable } from "svelte/store";
import { MatrixTools } from "../../lib/matrices/MatrixTools";
import { MapManager } from "../../lib/maplibre/MapManager";
export class OdEditModalManager {
    constructor(data) {
        this.nameStores = [];
        this.selectedLocation = -1;
        this.data = data;
        this.matrixStore = writable(this.data.matrix);
        this.refreshNameStores();
        const mapOptions = {
            imageryLayers: [],
            baseLayer: new ShortCutClassicLayer(),
        };
        this.mapManager = new MapManager(mapOptions);
        this.rebuildIndex();
        this.matrixStore.subscribe(_ => {
            this.refreshNameStores();
            this.refreshMapData();
            this.rebuildIndex();
        });
    }
    /**
     * Gets the matrix store.
     *
     * @returns
     */
    getMatrix() {
        return this.matrixStore;
    }
    getAsCSV() {
        let csv = "";
        const newLine = "\n";
        const matrix = get(this.matrixStore);
        csv += `"${matrix.name ?? ""}",`;
        csv += this.nameStores.map((s, i) => {
            const name = get(s) ?? `${i}`;
            return `"${name}"`;
        }).join(",");
        csv += newLine;
        for (let x = 0; x < matrix.locations.length; x++) {
            const name = get(this.nameStores[x]) ?? `${x}`;
            csv += `"${name}",`;
            for (let y = 0; y < matrix.locations.length; y++) {
                const value = this.getValue(x, y);
                csv += `${value},`;
            }
            csv += newLine;
        }
        return csv;
    }
    refreshNameStores() {
        const matrix = get(this.matrixStore);
        if (this.nameStores.length > matrix.locations.length)
            this.nameStores.length = matrix.locations.length;
        for (let i = 0; i < matrix.locations.length; i++) {
            while (this.nameStores.length <= i)
                this.nameStores.push(writable(""));
            const location = matrix.locations[i];
            const name = location.properties?.name ?? `${i}`;
            if (name === get(this.nameStores[i]))
                continue;
            this.nameStores[i].set(name);
        }
    }
    getNameStore(i) {
        return this.nameStores[i];
    }
    setName(i, value) {
        const matrix = get(this.matrixStore);
        const location = matrix.locations[i];
        location.properties ??= {};
        location.properties.name = value;
        this.matrixStore.set(matrix);
    }
    rebuildIndex() {
        this.index = new Dictionary();
        this.data.matrix.elements.forEach(mv => {
            const key = `${mv.fromIdx}-${mv.toIdx}`;
            this.index.set(key, mv);
        });
    }
    async initializeMap(container) {
        await this.mapManager.initialize(container, async () => {
            this.mapManager.addControl(new NavigationControl({}), "top-right");
            this.mapManager.addControl(new FullscreenControl({}), "top-right");
            this.mapManager.addSource("matrix", {
                type: "geojson",
                data: turf.featureCollection([]),
            });
            this.mapManager.addLayer({
                id: "movement-lines",
                type: "line",
                source: "matrix",
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                },
                paint: {
                    "line-color": "#000",
                    "line-opacity": 0.2,
                    "line-width": 2,
                    "line-offset": 8,
                },
            });
            this.mapManager.addLayer({
                id: "movement-labels",
                type: "symbol",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                    "text-field": "{count}",
                    "text-size": 15,
                    "symbol-placement": "line-center",
                    "text-offset": [0.5, 0.5],
                },
                paint: {
                    "text-halo-width": 2,
                    "text-halo-color": "#fff",
                },
            });
            this.mapManager.addLayer({
                id: "locations-back",
                type: "circle",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#000000",
                    "circle-radius": 14,
                },
                filter: ["all", ["!has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.addLayer({
                id: "locations",
                type: "circle",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#ffc000",
                    "circle-stroke-width": 0,
                    "circle-radius": 12,
                },
                filter: ["all", ["!has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.addLayer({
                id: "locations-labels-id",
                type: "symbol",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                    "text-field": "{id}",
                    "text-size": 15,
                },
                paint: {},
                filter: ["all", ["!has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.addLayer({
                id: "locations-name-back",
                type: "circle",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#000000",
                    "circle-radius": 6,
                },
                filter: ["all", ["has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.addLayer({
                id: "locations-name",
                type: "circle",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                },
                paint: {
                    "circle-color": "#ffc000",
                    "circle-stroke-width": 0,
                    "circle-radius": 4,
                },
                filter: ["all", ["has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.addLayer({
                id: "locations-labels-name",
                type: "symbol",
                source: "matrix",
                minzoom: 8,
                layout: {
                    visibility: "visible",
                    "text-field": "{name}",
                    "text-size": 15,
                    "text-anchor": "top",
                    "text-offset": [0, 0.5],
                },
                paint: {
                    "text-halo-width": 2,
                    "text-halo-color": "#fff",
                },
                filter: ["all", ["has", "name"], ["==", "type", "location"]]
            });
            this.mapManager.onLayer("mousedown", "locations", (e) => {
                this.onDown(e);
            });
            // this.mapManager.on("touchstart", "locations", (e) => {
            //     if (e.points.length !== 1) return;
            //     // Prevent the default map drag behavior.
            //     e.preventDefault();
            //     this.mapManager.on("touchmove", onMove);
            //     this.mapManager.once("touchend", onUp);
            // });
            this.mapManager.on("click", (e) => {
                const coords = e.lngLat;
                const bbox = [
                    [e.point.x - 5, e.point.y - 5],
                    [e.point.x + 5, e.point.y + 5],
                ];
                let selectedFeatures = this.mapManager.queryRenderedFeatures(bbox, {
                    layers: ["locations"],
                });
                selectedFeatures = selectedFeatures.filter(x => x.properties.type == "location");
                if (selectedFeatures.length === 0) {
                    this.data.matrix.locations.push({
                        latitude: coords.lat,
                        longitude: coords.lng,
                        properties: { type: "unkown" }
                    });
                    this.matrixStore.set(this.data.matrix);
                    return;
                }
                const toDelete = selectedFeatures[0].properties.id;
                // check if location has movements and remove.
                this.data.matrix.elements = this.data.matrix.elements.filter(x => {
                    return x.fromIdx != toDelete && x.toIdx != toDelete;
                });
                // remove location and update all movements.
                this.data.matrix.locations.splice(toDelete, 1);
                // update all movements.
                this.data.matrix.elements.forEach((x) => {
                    if (x.fromIdx > toDelete)
                        x.fromIdx--;
                    if (x.toIdx > toDelete)
                        x.toIdx--;
                });
                this.matrixStore.set(this.data.matrix);
            });
            this.refreshMapData();
        }, {
            zoom: this.data.zoom,
            center: this.data.center,
        });
    }
    refreshMapData() {
        if (!this.mapManager.loaded())
            return;
        const locationsSource = this.mapManager.getSource("matrix");
        if (typeof locationsSource === "undefined")
            return;
        const features = turf.featureCollection(MatrixTools.buildFeatures(this.data.matrix));
        locationsSource.setData(features);
        console.log("refreshing map data");
        console.log(features);
    }
    onMove(e) {
        const coords = e.lngLat;
        if (this.selectedLocation === -1)
            return;
        // Set a UI indicator for dragging.
        //canvas.style.cursor = "grabbing";
        // Update the Point feature in `geojson` coordinates
        // and call setData to the source layer `point` on it.
        const location = this.data.matrix.locations[this.selectedLocation];
        location.latitude = coords.lat;
        location.longitude = coords.lng;
        // rebuild features.
        this.refreshMapData();
    }
    onDown(e) {
        // Prevent the default map drag behavior.
        e.preventDefault();
        const features = e.features.filter(x => x.properties.type == "location");
        this.selectedLocation = features[0].properties.id;
        // canvas.style.cursor = "grab";
        this.mapManager.on("mousemove", (e) => {
            this.onMove(e);
        });
        this.mapManager.once("mouseup", (_) => {
            this.onUp();
        });
    }
    onUp() {
        this.selectedLocation = -1;
        this.matrixStore.set(this.data.matrix);
    }
    /**
     * Gets the number of locations.
     *
     * @returns The number of locations.
     */
    getCount() {
        return this.data.matrix.locations.length;
    }
    /**
     * Gets the value for the given od-pair.
     */
    getValue(from, to) {
        const key = `${from}-${to}`;
        return this.index.get(key)?.count ?? 0;
    }
    /**
     * Sets the value for the given od-pair.
     *
     * @param from
     * @param to
     * @param count
     * @returns
     */
    setValue(from, to, count) {
        const key = `${from}-${to}`;
        let value = this.index.get(key);
        if (typeof value === "undefined") {
            if (count === 0)
                return;
            value = {
                fromIdx: from,
                toIdx: to,
                count: count
            };
            this.data.matrix.elements.push(value);
            this.matrixStore.set(this.data.matrix);
            return;
        }
        if (value.count !== count) {
            if (count > 0) {
                value.count = count;
            }
            else {
                const i = this.data.matrix.elements.findIndex(x => x.fromIdx == from && x.toIdx == to);
                if (i < 0)
                    return;
                console.log("removing element");
                this.data.matrix.elements.splice(i, 1);
            }
            this.matrixStore.set(this.data.matrix);
        }
        return;
    }
}
